import React from "react";
import { MapPin, Building2, FileText, Globe, Briefcase, Store } from "lucide-react";

/**
 * Context banner shown at the top of every assessment/risk page (and the vendor dashboard):
 * a row of entity chips identifying where the user is — Region / Org / Deal / Business Unit /
 * App / Vendor. Render only the chips whose prop is provided:
 *   - Org-level assessments:        region + org
 *   - Deal assessments (non-app):   region + org + deal
 *   - Deal application assessments: region + org + deal + app
 *   - BU assessments (non-app):     region + org + businessUnit
 *   - BU application assessments:   region + org + businessUnit + app
 *   - Vendor dashboard:             region + org + vendor
 *
 * Colors follow the platform entity conventions: org blue, deal purple, app green,
 * business unit amber (VendorDashboard badge convention), vendor green (TPRM badge convention).
 */
const Chip = ({ icon: Icon, label, value, palette }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${palette.chip}`}>
    <Icon className="h-3.5 w-3.5" />
    <span className={`${palette.label} font-normal`}>{label}:</span>
    {value}
  </span>
);

const PALETTES = {
  region: { chip: "bg-gray-50 border border-gray-300 text-gray-600", label: "text-gray-400" },
  org: { chip: "bg-blue-50 border border-blue-200 text-blue-700", label: "text-blue-400" },
  deal: { chip: "bg-purple-50 border border-purple-200 text-purple-700", label: "text-purple-400" },
  businessUnit: { chip: "bg-amber-50 border border-amber-200 text-amber-700", label: "text-amber-400" },
  app: { chip: "bg-green-50 border border-green-200 text-green-700", label: "text-green-400" },
  vendor: { chip: "bg-green-50 border border-green-200 text-green-700", label: "text-green-400" },
};

const AssessmentContextHeader = ({ regionName, orgName, dealName, businessUnitName, appName, vendorName }) => {
  if (!regionName && !orgName && !dealName && !businessUnitName && !appName && !vendorName) return null;
  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm flex flex-wrap items-center gap-2">
      {regionName && <Chip icon={MapPin} label="Region" value={regionName} palette={PALETTES.region} />}
      {orgName && <Chip icon={Building2} label="Org" value={orgName} palette={PALETTES.org} />}
      {dealName && <Chip icon={FileText} label="Deal" value={dealName} palette={PALETTES.deal} />}
      {businessUnitName && <Chip icon={Briefcase} label="Business Unit" value={businessUnitName} palette={PALETTES.businessUnit} />}
      {appName && <Chip icon={Globe} label="App" value={appName} palette={PALETTES.app} />}
      {vendorName && <Chip icon={Store} label="Vendor" value={vendorName} palette={PALETTES.vendor} />}
    </div>
  );
};

export default AssessmentContextHeader;
